/* eslint-disable @typescript-eslint/no-explicit-any */
import Wrapper from '@/common/wrapper/Wrapper';
import { useRouter } from '@/hooks/hooks';
import { Button } from 'antd';
import style from './Analysis.module.less';
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AnalysisContext } from './AnalysisPortal';
import {addLogoPt, getFourRatesAnalysis} from '@/service/exam/analysis';
import { ColumnConfig, courseModuleTypeRecord, fourRatesColumns } from './const';
import { courseRecord, schoolAreaRecord } from '@/utils/const';
import { cloneDeep } from 'lodash';
import { exportFile } from 'table-xlsx';
import { filterNull } from '@/utils';
import layer from '@/utils/layer';
import { formatAnalysisTableRows, formatAnalysisTableValue } from './format';

const FourRates = () => {
  const { courseDcode } = useOutletContext<AnalysisContext>();
  const router = useRouter();
  const { examid, name } = router.getSearchParams();
  const [columns, setColumns] = useState(cloneDeep(fourRatesColumns));
  const [list, setList] = useState<FourRateAnalysisStat[]>([]);

  const load = async () => {
    try {
      layer.loading();
      const { data } = await getFourRatesAnalysis({
        examId: examid,
        coursetypeDcode: courseDcode
      });
      if (Object.keys(data).length === 0) {
        setList([]);
        return false;
      }
      if (data.scoreModuleTypeList.length > 0) {
        const children: ColumnConfig<FourRateAnalysisStat>[] = data.scoreModuleTypeList.map(i => ({
          title: courseModuleTypeRecord[i],
          dataIndex: i,
          isAvg: true
        }));
        children.push({
          title: `${courseRecord[courseDcode].slice(0, 1)}总`,
          dataIndex: `${courseDcode}FullScore`,
          isAvg: true,
          render: (_, p) => {
            // return Object.entries(p.moduleAvgScore || {}).reduce((a, [, value]) => a + value, 0).toFixed(2);
            return p.avgScore;
          }
        });
        setColumns(columns.map(i => {
          if (i.dataIndex === 'avgScore') return {
            ...i,
            children
          };
          return i;
        }));
      } else {
        setColumns(cloneDeep(fourRatesColumns));
      }

      // setList
      const newList: FourRateAnalysisStat[] = [
        ...data.schoolStatList.map(i => {
          return {
            ...i,
            fullScore: data.fullScore,
            ...data.scoreModuleTypeList.reduce((a, key) => { return { ...a, [key]: i.moduleAvgScore[key] || 0 }; }, {}),
            [`${courseDcode}FullScore`]: i.avgScore
          };
        }),
        ...data.areaStatList.map(i => {
          return {
            ...i,
            schoolShortname: schoolAreaRecord[i.areaCode],
            fullScore: data.fullScore,
            ...data.scoreModuleTypeList.reduce((a, key) => { return { ...a, [key]: i.moduleAvgScore[key] || 0 }; }, {}),
            [`${courseDcode}FullScore`]: i.avgScore
          };
        })
      ];
      if (data.amountStat) newList.push( {
        ...data.amountStat,
        ...data.amountStat.moduleAvgScore,
        [`${courseDcode}FullScore`]: data.amountStat.areaCode,
        fullScore: data.fullScore
      });
      setList(newList);
    } finally {
      layer.closeLoading();
    }
  };

  useEffect(() => {
    courseDcode && load();
  }, [courseDcode]);

  const secondRow = useMemo(() => {
    const c = columns.find(i => i.children && i.children.length > 0);
    if (c) return c.children as ColumnConfig<FourRateAnalysisStat>[];
    return [];
  }, [columns]);

  const bodyColumns = useMemo(() => {
    return columns.reduce((a, c) => {
      if (c.children && c.children.length > 0) return [...a, ...c.children];
      return [...a, c];
    }, [] as Omit<ColumnConfig<FourRateAnalysisStat>, 'active'>[]);
  }, [columns]);

  const downloadExcel = () => {
    const c = columns.reduce((a, curr) => {
      if (!curr.children || curr.children.length === 0) {
        if (curr.dataIndex === 'schoolCode') {
          return [...a, {
            title: curr.title,
            dataIndex: curr.dataIndex,
            width: curr.width || 80,
            render: (value: string, record: unknown, index: number) => {
              if (index === 0) {
                return {
                  children: value || '全市',
                  props: {
                    colSpan: c.length
                  }
                };
              } else {
                return value;
              }
            }
          }];
        } else {
          return [...a, {
            title: curr.title,
            dataIndex: curr.dataIndex,
            width: curr.width || 80
          }];
        }
      } else {
        return [...a, ...curr.children?.map(i => ({
          title: i.title + '-' + curr.title,
          width: 80,
          dataIndex: i.dataIndex
        })) as ColumnConfig<FourRateAnalysis>[]];
      }
    }, [] as any);


    const dataSource = [
      {
        ...list[0],
        schoolCode: decodeURI(name) + '-' + courseRecord[courseDcode] + '-四率统计'
      },
      c.reduce((a: any, curr: any) => {
        return { ...a, [curr.dataIndex]: curr.title };
      }, { maxCountScoreList: [] }),
      ...list.map(i => ({
        ...i,
        areaCode: schoolAreaRecord[i.areaCode] || '全市',
        maxCountScore: i.maxCountScoreList ? i.maxCountScoreList[0] : '',
        numberCount: `${i.totalStuCount}/${i.presentStuCount}/${i.absentStuCount}`
      }))
    ];

    exportFile({
      columns: c,
      dataSource: formatAnalysisTableRows(filterNull(dataSource), c) as unknown as any,
      showHeader: false,
      raw: true,
      onTxBodyRow: (_: unknown, index: number) => {
        if (index <= 1) {
          return {
            style: {
              borderStyle: 'thin',
              borderColorRgb: '000',
              alignmentHorizontal: 'center',
              fontBold: true,
              fontName: '宋体'
            }
          };
        }
        return {
          style: {
            borderStyle: 'thin',
            borderColorRgb: '000',
            alignmentHorizontal: 'center',
            fontName: '宋体'
          }
        };
      },
      fileName: decodeURI(name) + '-' + courseRecord[courseDcode] + '-四率统计.xlsx'
    });
    addLogoPt(examid, decodeURI(name) + '-' + courseRecord[courseDcode] + '-四率统计.xlsx');
  };

  return (
    <>
      <div className={style.title}>
        <div>四率统计</div>
        <Button onClick={downloadExcel}>导出</Button>
      </div>
      <div className={style.table}>
        <Wrapper>
          <div className={style.tableWrapper}>
            <table className={style.uniTable}>
              <thead>
                <tr>
                  {
                    columns.map(i => (
                      <th
                        key={i.title}
                        style={{width: `${i.width || 80}px`, height: `${secondRow.length > 0 ? '' : '50px'}`}}
                        rowSpan={(secondRow.length > 0 && i.dataIndex !== 'avgScore')? 2 : 1}
                        colSpan={(secondRow.length > 0 && i.dataIndex === 'avgScore')? 3 : 1}
                      >
                        {i.title}
                      </th>
                    ))
                  }
                </tr>
                {
                  secondRow.length > 0 && (
                    <tr>
                      {
                        secondRow.map(i => (
                          <th
                            key={i.title}
                            style={{width: `${i.width || 80}px`}}
                          >
                            {i.title}
                          </th>
                        ))
                      }
                    </tr>
                  )
                }
              </thead>
              <tbody>
                {
                  list.map((i, index) => (
                    <tr key={index}>{
                      bodyColumns.map(j => (
                        <td
                          key={j.title}
                          style={{width: `${j.width || 80}px`}}
                          className={i['areaCode'] === 'WHOLE_AREA_T' ? 'bolder' : ''}
                        >
                          {formatAnalysisTableValue(j.render ? j.render(i[j.dataIndex as string], i, index) : i[j.dataIndex as string], j) as unknown as any}
                        </td>
                      ))
                    }</tr>
                  ))
                }
                {
                  list.length === 0 && <tr><td colSpan={bodyColumns.length}>暂无数据</td></tr>
                }
              </tbody>
            </table>
          </div>
        </Wrapper>
      </div>
    </>
  );
};

export default FourRates;
